import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { products, companyInfo, productImages, productGallery } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useInView } from '../hooks/useInView';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, ShieldCheck, CheckCircle, Send, ArrowRight, Phone,
  Zap, Gauge, Wind, Droplets, Flame, Layers, Sparkles, FlaskConical, Leaf, Fuel, Link as LinkIcon
} from 'lucide-react';

const iconMap = {
  Zap, Gauge, Wind, Droplets, Flame, Layers, Sparkles, FlaskConical, Leaf, Fuel, Link: LinkIcon, Pickaxe: Sparkles
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug);
  const [ref, isInView] = useInView();
  const [formData, setFormData] = useState({
    companyName: '', contactPerson: '', phone: '', email: '', quantity: '', specifications: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set document title and meta tags for SEO - must be before any early returns
  useEffect(() => {
    if (product) {
      const seoTitle = product.name + " | " + product.brand + " | V.R. Enterprises - DOZZ Hoses";
      const seoDescription = "Buy " + product.name + " from V.R. Enterprises. " + product.shortDescription + " " + (product.certified ? "ISI Certified. " : "") + product.standard + ". Trusted manufacturer since 1984.";
      
      document.title = seoTitle;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', seoDescription);
      
      // Update OG tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', seoTitle);
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', seoDescription);
    } else {
      document.title = "Product Not Found | V.R. Enterprises";
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <h1 className="font-heading font-bold text-navy-900 text-2xl mb-4">Product Not Found</h1>
        <Link to="/" className="text-ind-red-600 font-medium hover:underline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>
    );
  }

  const productImage = productImages[product.slug];
  const IconComponent = iconMap[product.icon] || Zap;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        product_required: product.name,
        quantity: formData.quantity,
        specifications: formData.specifications,
        location: '',
      });

      const subject = encodeURIComponent(`Quote Request for ${product.name} - ${formData.companyName}`);
      const body = encodeURIComponent(
        `Product: ${product.name}\nBrand: ${product.brand}\nStandard: ${product.standard}\n\nCompany Name: ${formData.companyName}\nContact Person: ${formData.contactPerson}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nQuantity: ${formData.quantity}\nSpecifications: ${formData.specifications}\n`
      );
      window.location.href = `mailto:${companyInfo.emails[0]}?cc=${companyInfo.emails[1]}&subject=${subject}&body=${body}`;

      toast.success('Quote request saved! Opening email client.');
      setFormData({ companyName: '', contactPerson: '', phone: '', email: '', quantity: '', specifications: '' });
    } catch (err) {
      const subject = encodeURIComponent(`Quote Request for ${product.name} - ${formData.companyName}`);
      const body = encodeURIComponent(
        `Product: ${product.name}\nCompany: ${formData.companyName}\nContact: ${formData.contactPerson}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nQty: ${formData.quantity}\nSpecs: ${formData.specifications}\n`
      );
      window.location.href = `mailto:${companyInfo.emails[0]}?cc=${companyInfo.emails[1]}&subject=${subject}&body=${body}`;
      toast.success('Opening email client to send quote request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Related products
  const related = products.filter((p) => p.slug !== slug).slice(0, 4);

  return (
    <main ref={ref}>
      {/* Breadcrumb */}
      <div className="bg-navy-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-navy-400 hover:text-white">Home</Link>
            <span className="text-navy-600">/</span>
            <Link to="/#products" className="text-navy-400 hover:text-white">Products</Link>
            <span className="text-navy-600">/</span>
            <span className="text-navy-200 font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-navy-950 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className={`${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-ind-red-600/10 border border-ind-red-600/20 flex items-center justify-center">
                  <IconComponent className="w-6 h-6 text-ind-red-400" />
                </div>
                {product.certified && (
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1" /> ISI Certified
                  </Badge>
                )}
              </div>
              <h1 className="font-heading font-extrabold text-white text-3xl lg:text-4xl mb-3">{product.name}</h1>
              <p className="text-ind-red-400 font-semibold text-sm mb-2">{product.brand} • {product.standard}</p>
              <p className="text-navy-300 text-base leading-relaxed mb-6">{product.shortDescription}</p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => document.getElementById('product-inquiry')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-ind-red-600 hover:bg-ind-red-700 text-white font-bold px-6 h-11 rounded-lg shadow-lg shadow-ind-red-600/25">
                  {product.cta} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <a href={`tel:${companyInfo.phones[0]}`}>
                  <Button variant="outline" className="border-navy-600 text-navy-200 hover:bg-navy-800 hover:text-white h-11 rounded-lg">
                    <Phone className="w-4 h-4 mr-2" /> Call Now
                  </Button>
                </a>
              </div>
            </div>

            {/* Specs card + Product Image */}
            <div className={`${isInView ? 'animate-slide-in-right' : 'opacity-0'} space-y-5`} style={{ animationDelay: '0.2s' }}>
              {/* Product Image */}
              {productImages[product.slug] && (
                <div className="rounded-2xl overflow-hidden border border-navy-800 bg-white">
                  <img
                    src={productImages[product.slug]}
                    alt={product.name}
                    className="w-full h-56 object-cover"
                  />
                </div>
              )}
              {product.specs && (
                <div className="bg-navy-900/60 backdrop-blur-sm border border-navy-800 rounded-2xl p-6 lg:p-8">
                  <h3 className="font-heading font-bold text-white text-lg mb-4">Technical Specifications</h3>
                  <div className="space-y-3">
                    {Object.entries(product.specs).map(([key, val]) => (
                      <div key={key} className="flex items-start justify-between py-2 border-b border-navy-800 last:border-0">
                        <span className="text-sm text-navy-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-sm text-white font-medium text-right ml-4">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features & Applications */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Features */}
            <div className="bg-white rounded-xl border border-navy-100 p-6 lg:p-8">
              <h3 className="font-heading font-bold text-navy-900 text-xl mb-5">Key Features</h3>
              <div className="space-y-3">
                {product.features.map((feat) => (
                  <div key={feat} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-ind-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-navy-700">{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Applications */}
            <div className="bg-white rounded-xl border border-navy-100 p-6 lg:p-8">
              <h3 className="font-heading font-bold text-navy-900 text-xl mb-5">Applications</h3>
              <div className="grid grid-cols-2 gap-3">
                {product.applications.map((app) => (
                  <div key={app} className="flex items-center gap-2 bg-navy-50/60 rounded-lg p-3 border border-navy-100">
                    <div className="w-2 h-2 rounded-full bg-ind-red-600 flex-shrink-0" />
                    <span className="text-sm text-navy-700 font-medium">{app}</span>
                  </div>
                ))}
              </div>

              {/* Types/Classes if available */}
              {(product.types || product.classes) && (
                <div className="mt-6 pt-5 border-t border-navy-100">
                  <h4 className="font-heading font-bold text-navy-900 text-base mb-3">
                    {product.classes ? 'Available Classes' : 'Available Types'}
                  </h4>
                  <div className="space-y-2">
                    {(product.classes || product.types || []).map((item) => (
                      <div key={item.name} className="bg-navy-50/60 rounded-lg p-3 border border-navy-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-navy-800">{item.name}</span>
                          <span className="text-xs font-medium text-ind-red-600">
                            {item.voltage || item.pressure || ''}
                          </span>
                        </div>
                        {(item.burstPressure || item.thickness || item.reinforcement || item.use || item.dielectricStrength || item.sizes) && (
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                            {item.thickness && <span className="text-xs text-navy-500">Thickness: {item.thickness}</span>}
                            {item.dielectricStrength && <span className="text-xs text-navy-500">Dielectric: {item.dielectricStrength}</span>}
                            {item.burstPressure && <span className="text-xs text-navy-500">Burst: {item.burstPressure}</span>}
                            {item.reinforcement && <span className="text-xs text-navy-500">{item.reinforcement}</span>}
                            {item.use && <span className="text-xs text-navy-500">{item.use}</span>}
                            {item.sizes && <span className="text-xs text-navy-500">Sizes: {item.sizes}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Size / Specification Table */}
      {product.sizeTable && product.sizeTable.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="font-heading font-bold text-navy-900 text-xl mb-6">Size & Pressure Specifications</h3>
            <div className="overflow-x-auto rounded-xl border border-navy-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-navy-950 text-white">
                    {Object.keys(product.sizeTable[0]).map((key) => (
                      <th key={key} className="px-4 py-3 text-left font-heading font-semibold text-xs uppercase tracking-wider">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {product.sizeTable.map((row, idx) => (
                    <tr key={idx} className={`border-b border-navy-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-navy-50/40'}`}>
                      {Object.values(row).map((val, i) => (
                        <td key={i} className={`px-4 py-3 ${i === 0 ? 'font-semibold text-navy-900' : 'text-navy-600'}`}>
                          {val}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Product Gallery */}
      {productGallery[product.slug] && productGallery[product.slug].length > 0 && (
        <section className="py-12 bg-[hsl(210,20%,98%)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="font-heading font-bold text-navy-900 text-xl mb-6">Product Gallery</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {productImages[product.slug] && (
                <div className="rounded-xl overflow-hidden border border-navy-100 bg-white">
                  <img src={productImages[product.slug]} alt={product.name} className="w-full h-52 object-cover" />
                  <div className="p-3 bg-navy-50 border-t border-navy-100">
                    <p className="text-sm font-medium text-navy-700">{product.name} - Main View</p>
                  </div>
                </div>
              )}
              {productGallery[product.slug].map((item, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden border border-navy-100 bg-white">
                  <img src={typeof item === 'string' ? item : item.url} alt={typeof item === 'string' ? `${product.name} ${idx + 2}` : item.label} className="w-full h-52 object-cover" />
                  <div className="p-3 bg-navy-50 border-t border-navy-100">
                    <p className="text-sm font-medium text-navy-700">{typeof item === 'string' ? `${product.name} - View ${idx + 2}` : item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Product Inquiry Form */}
      <section id="product-inquiry" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-navy-950 rounded-2xl p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-40" />
            <div className="relative">
              <h3 className="font-heading font-bold text-white text-xl mb-2">{product.cta}</h3>
              <p className="text-navy-400 text-sm mb-6">Fill in your requirements and we'll provide pricing within 24 hours.</p>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs">Company Name *</Label>
                    <Input required value={formData.companyName} onChange={(e) => handleChange('companyName', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg" placeholder="Company name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs">Contact Person *</Label>
                    <Input required value={formData.contactPerson} onChange={(e) => handleChange('contactPerson', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg" placeholder="Full name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs">Phone *</Label>
                    <Input required type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg" placeholder="+91 XXXXXXXXXX" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs">Email *</Label>
                    <Input required type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg" placeholder="email@company.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-navy-300 text-xs">Quantity</Label>
                    <Input value={formData.quantity} onChange={(e) => handleChange('quantity', e.target.value)}
                      className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 h-10 rounded-lg" placeholder="e.g., 500 meters" />
                  </div>
                </div>
                <div className="mb-4 space-y-1.5">
                  <Label className="text-navy-300 text-xs">Technical Specifications</Label>
                  <Textarea value={formData.specifications} onChange={(e) => handleChange('specifications', e.target.value)}
                    className="bg-navy-900/60 border-navy-700 text-white placeholder:text-navy-500 rounded-lg" placeholder="Size, pressure, material requirements..." rows={3} />
                </div>
                <Button type="submit" disabled={isSubmitting}
                  className="w-full bg-ind-red-600 hover:bg-ind-red-700 text-white font-bold h-12 rounded-lg text-base shadow-lg shadow-ind-red-600/25">
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h3 className="font-heading font-bold text-navy-900 text-xl mb-6">Other Products</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((p) => {
              const RelIcon = iconMap[p.icon] || Zap;
              return (
                <Link to={`/products/${p.slug}`} key={p.slug}
                  className="card-hover group bg-white rounded-xl border border-navy-100 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center">
                      <RelIcon className="w-4 h-4 text-navy-600 group-hover:text-ind-red-600" />
                    </div>
                    {p.certified && (
                      <Badge variant="outline" className="text-[9px] border-green-200 text-green-700 bg-green-50">
                        ISI
                      </Badge>
                    )}
                  </div>
                  <h4 className="font-heading font-bold text-navy-900 text-sm mb-1 group-hover:text-ind-red-600">{p.name}</h4>
                  <p className="text-xs text-navy-500">{p.brand}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
